import { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";

/**
 * Per-investigation metadata for a single Rad/EP investigation.
 * Both fields default to empty strings in the UI (the API stores null
 * for absent values; the payload transform converts "" → null).
 */
export interface InvDetailEntry {
  regionAndSide: string;
  clinicalNotes: string;
}

export type InvDetailsMap = Record<string, InvDetailEntry>;

// ---------------------------------------------------------------------------
// Single-investigation edit dialog
// ---------------------------------------------------------------------------

interface SingleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  investigationName: string;
  investigationCategory: string;
  value: InvDetailEntry;
  onSave: (value: InvDetailEntry) => void;
}

/**
 * Dialog for editing "Region and Side" and "Clinical Notes" for a single
 * Radiology or Electrophysiology investigation. Opens automatically when
 * a Rad/EP test is added from search, or when the pencil icon on a badge
 * is clicked.
 */
export function InvestigationDetailDialog({
  open, onOpenChange, investigationName, investigationCategory, value, onSave,
}: SingleDialogProps) {
  const [regionAndSide, setRegionAndSide] = useState(value.regionAndSide);
  const [clinicalNotes, setClinicalNotes] = useState(value.clinicalNotes);
  const regionRef = useRef<HTMLInputElement>(null);

  // Sync local state when the dialog opens (value may change between opens).
  useEffect(() => {
    if (open) {
      setRegionAndSide(value.regionAndSide);
      setClinicalNotes(value.clinicalNotes);
      // Focus the Region & Side field shortly after open.
      setTimeout(() => regionRef.current?.focus(), 50);
    }
  }, [open, value.regionAndSide, value.clinicalNotes]);

  const handleSave = () => {
    onSave({ regionAndSide: regionAndSide.trim(), clinicalNotes: clinicalNotes.trim() });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Investigation Details</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-ink">{investigationName}</span>
            <span className="ml-2 rounded bg-cream-100 px-1.5 py-0.5 text-xs text-muted">{investigationCategory}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="inv-region">Region & Side</Label>
            <Input
              id="inv-region"
              ref={regionRef}
              value={regionAndSide}
              onChange={(e) => setRegionAndSide(e.target.value)}
              placeholder="e.g. Right knee, Left shoulder…"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="inv-notes">Clinical Notes</Label>
            <Textarea
              id="inv-notes"
              value={clinicalNotes}
              onChange={(e) => setClinicalNotes(e.target.value)}
              placeholder="Clinical context for this investigation…"
              className="h-24 resize-none"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Batch dialog — collects details for multiple Rad/EP investigations at once
// (used when a template adds several Rad/EP tests)
// ---------------------------------------------------------------------------

interface BatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entries: { id: string; name: string; category: string }[];
  existingDetails: InvDetailsMap;
  onSave: (details: InvDetailsMap) => void;
}

/**
 * Dialog for collecting Region & Side / Clinical Notes for multiple Rad/EP
 * investigations at once (e.g. when a template adds several Rad/EP tests).
 * Shows a scrollable list with two text fields per investigation. "Add"
 * saves all entered data; investigations with no data entered are skipped
 * (the row is omitted from the print table when both fields are empty).
 */
export function InvestigationBatchDetailsDialog({
  open, onOpenChange, entries, existingDetails, onSave,
}: BatchDialogProps) {
  // Local working copy: investigationId → { regionAndSide, clinicalNotes }
  const [local, setLocal] = useState<InvDetailsMap>({});
  const firstRegionRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      // Seed with existing details (if any) so re-editing doesn't wipe data.
      const seed: InvDetailsMap = {};
      for (const e of entries) {
        const ex = existingDetails[e.id];
        seed[e.id] = ex
          ? { regionAndSide: ex.regionAndSide, clinicalNotes: ex.clinicalNotes }
          : { regionAndSide: "", clinicalNotes: "" };
      }
      setLocal(seed);
      setTimeout(() => firstRegionRef.current?.focus(), 50);
    }
  }, [open, entries, existingDetails]);

  const handleSave = () => {
    // Only save entries that have at least one non-empty field.
    const filtered: InvDetailsMap = {};
    for (const [id, v] of Object.entries(local)) {
      if (v.regionAndSide.trim() || v.clinicalNotes.trim()) {
        filtered[id] = { regionAndSide: v.regionAndSide.trim(), clinicalNotes: v.clinicalNotes.trim() };
      }
    }
    onSave(filtered);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Investigation Details</DialogTitle>
          <DialogDescription>
            Enter Region & Side and Clinical Notes for the following {entries.length} investigation{entries.length === 1 ? "" : "s"}. Fields left blank will be omitted from the print.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[50vh]">
          <div className="space-y-4 px-1 py-1">
            {entries.map((e, idx) => (
              <div key={e.id} className="space-y-2 rounded-md border border-cream-200 bg-cream-50/50 p-3 dark:border-dark-700 dark:bg-dark-800/50">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-ink">{idx + 1}. {e.name}</span>
                  <span className="rounded bg-cream-100 px-1.5 py-0.5 text-xs text-muted">{e.category}</span>
                </div>
                <div className="grid grid-cols-1 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted">Region & Side</Label>
                    <Input
                      ref={idx === 0 ? firstRegionRef : undefined}
                      value={local[e.id]?.regionAndSide ?? ""}
                      onChange={(ev) => setLocal((p) => ({
                        ...p,
                        [e.id]: { ...(p[e.id] ?? { regionAndSide: "", clinicalNotes: "" }), regionAndSide: ev.target.value },
                      }))}
                      placeholder="e.g. Right knee, Left shoulder…"
                      className="h-8"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted">Clinical Notes</Label>
                    <Textarea
                      value={local[e.id]?.clinicalNotes ?? ""}
                      onChange={(ev) => setLocal((p) => ({
                        ...p,
                        [e.id]: { ...(p[e.id] ?? { regionAndSide: "", clinicalNotes: "" }), clinicalNotes: ev.target.value },
                      }))}
                      placeholder="Clinical context…"
                      className="h-16 resize-none"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
